// src/store/modules/tenant.js

export default {
  namespaced: true,
  state: () => ({
    current_tenant_slug: localStorage.getItem('current_tenant_slug') || null,
    tenantAccess: {},
  }),
  mutations: {
    SET_TENANT_SLUG(state, tenant_slug) {
      // Clear tenant-specific localStorage on tenant switch
      if (state.current_tenant_slug && state.current_tenant_slug !== tenant_slug) {
        localStorage.removeItem('entity_hierarchy_nav_nav')
      }
      state.current_tenant_slug = tenant_slug
      localStorage.setItem('current_tenant_slug', tenant_slug)
    },
    CLEAR_TENANT_SLUG(state) {
      state.current_tenant_slug = null
      localStorage.removeItem('current_tenant_slug')
    },
    SET_TENANT_ACCESS(state, tenantAccess) {
      state.tenantAccess = tenantAccess
      // If current_tenant_slug is not present in tenantAccess, pick the first available
      const slugs = Object.keys(tenantAccess)
      if (!slugs.includes(state.current_tenant_slug) && slugs.length > 0) {
        state.current_tenant_slug = slugs[0]
        localStorage.setItem('current_tenant_slug', slugs[0])
      }
    },
    UPDATE_POSTHOG_FLAGS(state, flags) {
      const slug = state.current_tenant_slug
      if (slug && state.tenantAccess[slug]) {
        state.tenantAccess[slug].posthog_flags = flags
      }
    },
  },
  getters: {
    availableTenants: state => Object.entries(state.tenantAccess).map(([slug, info]) => ({
      slug,
      name: info.tenant_name,
    })),
    current_tenant_slug: state => state.current_tenant_slug,
    current_tenant_name: state => {
      const info = state.tenantAccess[state.current_tenant_slug]
      return info ? info.tenant_name : null
    },
    actual_tenant_roles: state => {
      const info = state.tenantAccess[state.current_tenant_slug]
      return info ? info.roles : []
    },
    current_tenant_roles: (state, getters, rootState, rootGetters) => {
      const preview = rootGetters['rolePreview/activeRoles']
      if (preview !== null) return preview
      return getters.actual_tenant_roles
    },
    hasRole: (state, getters) => (role) => {
      return getters.current_tenant_roles.includes(role)
    },
    isManagerViewer: (state, getters) => getters.hasRole('manager_viewer'),
    isPortalUser: (state, getters) => getters.hasRole('portal'),
    isRestrictedToPortal: (state, getters) => {
      const info = state.tenantAccess[state.current_tenant_slug]
      if (!info) return false
      return info.roles.length === 1 && getters.isPortalUser
    },
    isExternalOnly: (state) => {
      const info = state.tenantAccess[state.current_tenant_slug]
      if (!info || !info.features) return false
      const features = info.features
      if (!features.includes('module_external')) return false
      const mainModules = ['module_insights', 'module_engage', 'module_manage', 'module_patient', 'module_admin', 'module_care_management', 'module_quality']
      return !features.some(f => mainModules.includes(f))
    },
    // Per-tenant display noun for care-management "programs". IHCI has an
    // org-wide aversion to "program" and uses "opportunity" (their internal
    // term). Every other tenant keeps "Programs". Single source of truth so the
    // rename never leaks across tenants (Care Advisor call 2026-07-01, ask #9).
    opportunityNoun: (state) => (state.current_tenant_slug === 'ihci' ? 'Opportunity' : 'Program'),
    opportunityNounPlural: (state) => (state.current_tenant_slug === 'ihci' ? 'Opportunities' : 'Programs'),
    isTenantAdmin: (state, getters) => getters.hasRole('admin'),
    isSecurityUser: (state, getters) => getters.hasRole('security'),
    isOnlyReadOnlyUser: (state, getters) => {
      const info = state.tenantAccess[state.current_tenant_slug]
      if (!info) return false
      return info.roles.length === 1 && getters.hasRole('readonly')
    },
  },
  actions: {
    setTenant_slug({ commit, dispatch }, tenant_slug) {
      commit('SET_TENANT_SLUG', tenant_slug)
      // Clear any stale admin-view config from the previous tenant. Admin
      // views (Config_Roles, user management, preview dropdown) now lazy-load
      // featureConfig on mount / open, so no prefetch is needed here.
      dispatch('featureConfig/clearConfig', null, { root: true })
      // Role preview role_ids are per-tenant — a preview of "medical_director"
      // in Diverge is meaningless once the user is on Vytalize. Drop it so
      // canAccessFeature stops evaluating against stranded role_ids.
      dispatch('rolePreview/disablePreview', null, { root: true })
    },
    clearTenant_slug({ commit }) {
      commit('CLEAR_TENANT_SLUG')
    },
    updateTenantsFromUser({ commit }, user) {
      const tenantAccess = user?.tenant_access || {}
      commit('SET_TENANT_ACCESS', tenantAccess)

      if (import.meta.env.DEV) {
        const flagsByTenant = {}
        for (const [slug, info] of Object.entries(tenantAccess)) {
          if (info.posthog_flags) flagsByTenant[slug] = info.posthog_flags
        }
        if (Object.keys(flagsByTenant).length) {
          console.log('[PostHog flags]', flagsByTenant)
        }
      }
    },
  }
}
